import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Spin, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import PageLayout from "components/common/pageLayout/pageLayout";
import { Title, SelectInput } from "components/common";
import {
  useGetDashboardV2StatsQuery,
  useGetDashboardV2PerformanceTrendQuery,
  useGetActiveUsersQuery,
} from "../../api";
import { DASHBOARD_USE_MOCK_DATA } from "utils/featureFlags";
import { mockStats, mockTrendData } from "../../mockData";
import DashboardKPIsV2 from "./DashboardKPIsV2";
import DashboardStreakPanel from "./DashboardStreakPanel";
import DashboardActivityHeatmap from "./DashboardActivityHeatmap";
import DashboardAnalyticsRow from "./DashboardAnalyticsRow";
import DashboardQuizzesV2 from "./DashboardQuizzesV2";
import styles from "./index.module.less";

const PERSONA_TOOLTIP =
  "View dashboard metrics as another user. This filter applies to all sections below.";

// ── Derived data helpers ───────────────────────────────────────────────────

const computeCurrentStreak = (accuracyTrend = []) => {
  if (!accuracyTrend.length) return 0;
  const uniqueDates = [
    ...new Set(
      accuracyTrend
        .map((item) => {
          const d = new Date(item.date);
          return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
        })
        .filter(Boolean)
    ),
  ].sort();
  if (!uniqueDates.length) return 0;
  const lastActive = uniqueDates[uniqueDates.length - 1];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const mostRecent = new Date(lastActive);
  mostRecent.setHours(0, 0, 0, 0);
  if (Math.round((today - mostRecent) / 86400000) > 1) return 0;
  let streak = 1;
  for (let i = uniqueDates.length - 2; i >= 0; i--) {
    const curr = new Date(uniqueDates[i + 1]);
    const prev = new Date(uniqueDates[i]);
    if (Math.round((curr - prev) / 86400000) === 1) streak++;
    else break;
  }
  return streak;
};

// Compute accuracy direction: compare avg of last 3 entries vs the 3 before that
const computeAccuracyDirection = (accuracyTrend = []) => {
  if (accuracyTrend.length < 4) return "neutral";
  const sorted = [...accuracyTrend].sort((a, b) => new Date(a.date) - new Date(b.date));
  const recent = sorted.slice(-3).map((i) => parseFloat(i.accuracy) || 0);
  const older = sorted.slice(-6, -3).map((i) => parseFloat(i.accuracy) || 0);
  if (!older.length) return "neutral";
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
  if (recentAvg > olderAvg + 2) return "up";
  if (recentAvg < olderAvg - 2) return "down";
  return "neutral";
};

// Which of the last 7 calendar days had at least one quiz
const computeLast7DaysActivity = (accuracyTrend = []) => {
  const activeDates = new Set(
    accuracyTrend
      .map((item) => {
        const d = new Date(item.date);
        return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
      })
      .filter(Boolean)
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(today);
    day.setDate(day.getDate() - (6 - i));
    return activeDates.has(day.toISOString().split("T")[0]);
  });
};

// Accuracy % per specialty derived from specialityPerformance
const computeSpecialityAccuracyMap = (specialityPerformance = {}) => {
  const map = {};
  Object.entries(specialityPerformance).forEach(([specialty, obj]) => {
    const total = (obj?.correct ?? 0) + (obj?.incorrect ?? 0);
    map[specialty] = total > 0 ? ((obj?.correct ?? 0) / total) * 100 : 0;
  });
  return map;
};

// Raw correct / incorrect / total per specialty for stacked bar in KPI cards
const computeSpecialityCountsMap = (specialityPerformance = {}) => {
  const map = {};
  Object.entries(specialityPerformance).forEach(([specialty, obj]) => {
    const correct = obj?.correct ?? 0;
    const incorrect = obj?.incorrect ?? 0;
    map[specialty] = { correct, incorrect, total: correct + incorrect };
  });
  return map;
};

// Quizzes attempted in the last 30 days (uses already-date-filtered latestQuizzes)
const computeRecentQuizCount = (latestQuizzes = []) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  return latestQuizzes.filter((q) => q.date && new Date(q.date) >= cutoff).length;
};

// Best and worst accuracy scores across recent quizzes
const computeScoreRange = (latestQuizzes = []) => {
  const scores = latestQuizzes
    .map((q) => parseFloat(q.accuracy ?? 0))
    .filter((s) => !isNaN(s));
  if (!scores.length) return { high: null, low: null };
  return { high: Math.max(...scores), low: Math.min(...scores) };
};

const PASS_THRESHOLD = 60;

// Passing Quiz Streak — consecutive quizzes (sorted newest first) with accuracy ≥ threshold
// Works on date-filtered latestQuizzes so it already respects the period filter
const computePassStreak = (latestQuizzes = []) => {
  const sorted = [...latestQuizzes]
    .filter((q) => q.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  let streak = 0;
  for (const quiz of sorted) {
    if (parseFloat(quiz.accuracy ?? 0) >= PASS_THRESHOLD) streak++;
    else break;
  }
  return streak;
};

// Last N quiz results as boolean array (true = pass) for the right-panel dots
const computeRecentQuizResults = (latestQuizzes = [], count = 7) =>
  [...latestQuizzes]
    .filter((q) => q.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, count)
    .reverse() // oldest → newest so dots read left-to-right
    .map((q) => parseFloat(q.accuracy ?? 0) >= PASS_THRESHOLD);

// ── Component ──────────────────────────────────────────────────────────────
const DashboardV2 = () => {
  const user = useSelector((state) => state?.app?.user?.info);
  const currentUserId = user?.user_code ?? user?._id;
  const isAdmin = user?.user_role?.toLowerCase() === "admin";

  const [selectedPersonaId, setSelectedPersonaId] = useState(user?.user_code ?? user?._id);
  const [kpiDateRange, setKpiDateRange] = useState(null);

  const userId = selectedPersonaId ?? currentUserId;

  const statsQueryBody = useMemo(() => {
    const body = { userId };
    if (kpiDateRange?.[0] && kpiDateRange?.[1]) {
      body.startDate = kpiDateRange[0].toISOString();
      body.endDate = kpiDateRange[1].toISOString();
    }
    return body;
  }, [userId, kpiDateRange]);

  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
  } = useGetDashboardV2StatsQuery(statsQueryBody, {
    skip: !userId || DASHBOARD_USE_MOCK_DATA,
    refetchOnMountOrArgChange: true,
  });

  // Trend API also receives date range so specialty & quiz data are date-filtered
  const trendQueryBody = useMemo(() => {
    const body = { userId };
    if (kpiDateRange?.[0] && kpiDateRange?.[1]) {
      body.startDate = kpiDateRange[0].toISOString();
      body.endDate   = kpiDateRange[1].toISOString();
    }
    return body;
  }, [userId, kpiDateRange]);

  const {
    data: trendData,
    isLoading: trendLoading,
    isFetching: trendFetching,
  } = useGetDashboardV2PerformanceTrendQuery(trendQueryBody, {
    skip: !userId || DASHBOARD_USE_MOCK_DATA,
    refetchOnMountOrArgChange: true,
  });

  // When mock flag is ON, swap in mock data and clear loading states
  const resolvedStats     = DASHBOARD_USE_MOCK_DATA ? mockStats     : stats;
  const resolvedTrendData = DASHBOARD_USE_MOCK_DATA ? mockTrendData : trendData;
  const resolvedStatsLoading  = DASHBOARD_USE_MOCK_DATA ? false : statsLoading;
  const resolvedTrendLoading  = DASHBOARD_USE_MOCK_DATA ? false : trendLoading;
  const resolvedStatsFetching = DASHBOARD_USE_MOCK_DATA ? false : statsFetching;
  const resolvedTrendFetching = DASHBOARD_USE_MOCK_DATA ? false : trendFetching;

  const { data: userList = [] } = useGetActiveUsersQuery(undefined, {
    skip: !isAdmin,
    refetchOnMountOrArgChange: true,
  });

  const personaOptions = useMemo(
    () =>
      userList.map((u) => ({
        value: u.user_code,
        label:
          user.user_code === u.user_code
            ? [u.firstName, u.lastName].filter(Boolean).join(" ").trim() + " (ME)"
            : [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.user_code,
      })),
    [userList]
  );

  // Derived values — all computed from existing API data, no new endpoints
  const accuracyTrend        = resolvedTrendData?.accuracyTrend        ?? [];
  const latestQuizzes        = resolvedTrendData?.latestQuizzes        ?? [];
  const specialityPerformance = resolvedTrendData?.specialityPerformance ?? {};

  const passStreak = useMemo(() => computePassStreak(latestQuizzes), [latestQuizzes]);
  const scoreRange = useMemo(() => computeScoreRange(latestQuizzes), [latestQuizzes]);

  const enrichData = useMemo(
    () => ({
      accuracyDirection:      computeAccuracyDirection(accuracyTrend),
      last7DaysActivity:      computeLast7DaysActivity(accuracyTrend),
      specialityAccuracyMap:  computeSpecialityAccuracyMap(specialityPerformance),
      specialityCountsMap:    computeSpecialityCountsMap(specialityPerformance),
      recentQuizCount:        computeRecentQuizCount(latestQuizzes),
      totalQuizzes:           resolvedStats?.totalQuizzes ?? 0,
      passStreak,
      recentQuizResults:      computeRecentQuizResults(latestQuizzes),
      scoreHigh:              scoreRange.high,
      scoreLow:               scoreRange.low,
    }),
    [accuracyTrend, specialityPerformance, latestQuizzes, resolvedStats?.totalQuizzes, passStreak, scoreRange]
  );

  const isLoading = resolvedStatsLoading || resolvedTrendLoading;

  if (isLoading && !resolvedStats && !resolvedTrendData) {
    return (
      <PageLayout
        showSubMenu={false}
        label={
          <Title level={2} style={{ margin: 0 }}>
            Dashboard
          </Title>
        }
      >
        <div className={styles.loadingWrap}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      showSubMenu={false}
      label={
        <Title level={2} style={{ margin: 0 }}>
          Dashboard
        </Title>
      }
      renderCustomActions={() => (
        <>
          {isAdmin && (
            <div className={styles.personaFilterWrap}>
              <span className={styles.personaLabel}>View as</span>
              <Tooltip title={PERSONA_TOOLTIP} placement="bottom">
                <InfoCircleOutlined className={styles.personaInfoIcon} />
              </Tooltip>
              <div className={styles.personaSelectWrap}>
                <SelectInput
                  className={styles.personaSelectInput}
                  placeholder="Select a User"
                  value={selectedPersonaId ?? ""}
                  allowClear={false}
                  showSearch
                  options={personaOptions}
                  onChange={(value) =>
                    setSelectedPersonaId(
                      value !== undefined && value !== "" ? value : null
                    )
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    >
      <div className={styles.content}>
        {/* Row 1 — KPI section (unified 6-card grid, all date-filtered) */}
        <DashboardKPIsV2
          stats={resolvedStats}
          passStreak={passStreak}
          loading={resolvedStatsLoading}
          isFetching={resolvedStatsFetching}
          dateRange={kpiDateRange}
          onDateRangeChange={setKpiDateRange}
          enrichData={enrichData}
        />

        {/* Row 2 — Study streak (streak stats use parent accuracyTrend;
                       chart has its own API call to /dashboard/v2/performance-chart) */}
        <DashboardStreakPanel accuracyTrend={accuracyTrend} />

        {/* Row 3 — Activity heatmap (last 12 months, own API call) */}
        <DashboardActivityHeatmap />

        {/* Row 4 — Score distribution + Performance by attribute (both self-contained) */}
        <DashboardAnalyticsRow />

        {/* Row 4 — Latest quizzes table */}
        <DashboardQuizzesV2
          latestQuizzes={latestQuizzes}
          loading={resolvedTrendLoading}
          isFetching={resolvedTrendFetching}
        />
      </div>
    </PageLayout>
  );
};

export default DashboardV2;
